import { useState, type Dispatch, type SetStateAction, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import client from '@/api/client';
import type { Tenant } from '@/api/types';
import { useAuth } from '@/auth/AuthContext';
import { Building2, Home } from 'lucide-react';
import { Select } from '@/components/ui/Select';

export const TENANT_STATUS_COLORS: Record<string, string> = {
  ACTIVE:      'bg-green-100 text-green-800',
  RESERVED:    'bg-amber-100 text-amber-800',
  CHECKED_OUT: 'bg-gray-100 text-gray-600',
  CANCELLED:   'bg-red-100 text-red-700',
};

export function fmt(n: number) {
  return '₹' + Number(n).toLocaleString('en-IN');
}

interface ActiveTenantResult {
  tenant: Tenant;
  activeTenants: Tenant[];
  tenantIdx: number;
  selectedTenantIdx: number;
  setSelectedTenantIdx: Dispatch<SetStateAction<number>>;
}

// Shared across every tenant-portal page: fetches the tenant's admissions, picks the
// currently selected one, and returns a `guard` element for the loading/empty states
// instead, so each page can bail out early with one `if ('guard' in portal) return portal.guard;`.
export function useTenantPortalGuard(): ActiveTenantResult | { guard: ReactNode } {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [selectedTenantIdx, setSelectedTenantIdx] = useState(0);

  const { data: tenants = [], isLoading } = useQuery<Tenant[]>({
    queryKey: ['tenant-portal-me'],
    queryFn: () => client.get('/api/tenant-portal/me').then(r => r as unknown as Tenant[]),
  });

  if (isLoading) {
    return { guard: <p className="text-gray-400 py-16 text-center">{t('common.loading')}</p> };
  }

  if (tenants.length === 0) {
    return {
      guard: (
        <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-sm border border-gray-100 p-10 text-center">
          <Home size={40} className="text-gray-300 mx-auto mb-3" />
          <h2 className="text-lg font-bold text-gray-900 mb-1">{t('dashboard.welcome', { name: user?.fullName })}</h2>
          <p className="text-sm text-gray-500">
            {t('tenant.notLinkedWithEmail', "You're not linked to a hostel yet — ask your hostel manager to confirm the email on your application matches your login email ({{email}}).", { email: user?.email })}
          </p>
        </div>
      ),
    };
  }

  // Only active admissions — checked-out/cancelled records are history, not the current portal
  const activeTenants = tenants.filter(tn => tn.status === 'ACTIVE' || tn.status === 'RESERVED');

  if (activeTenants.length === 0) {
    return {
      guard: (
        <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-sm border border-gray-100 p-10 text-center">
          <Home size={40} className="text-gray-300 mx-auto mb-3" />
          <h2 className="text-lg font-bold text-gray-900 mb-1">{t('tenant.noActiveAdmission', 'No active admission')}</h2>
          <p className="text-sm text-gray-500">
            {t('tenant.previousStayEnded', 'Your previous stay has ended. Once you are checked in to a new hostel your portal will be available here.')}
          </p>
        </div>
      ),
    };
  }

  const tenantIdx = selectedTenantIdx < activeTenants.length ? selectedTenantIdx : 0;

  return { tenant: activeTenants[tenantIdx], activeTenants, tenantIdx, selectedTenantIdx, setSelectedTenantIdx };
}

interface HostelSelectorProps {
  activeTenants: Tenant[];
  tenantIdx: number;
  onChange: (idx: number) => void;
}

// Only rendered when a tenant holds more than one active admission at once.
export function HostelSelector({ activeTenants, tenantIdx, onChange }: HostelSelectorProps) {
  const { t } = useTranslation();
  if (activeTenants.length <= 1) return null;
  return (
    <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2 shadow-sm">
      <Building2 size={14} className="text-amber-600 shrink-0" />
      <span className="text-xs text-gray-500 shrink-0">{t('tenant.hostelLabel', 'Hostel:')}</span>
      <Select
        value={tenantIdx}
        onChange={e => onChange(Number(e.target.value))}
        className="w-auto flex-1 border-0 shadow-none bg-transparent px-0 py-0 font-medium text-gray-700"
        options={activeTenants.map((at, i) => ({
          value: String(i),
          label: `${at.hostelName ?? at.hostelId.slice(0, 8)} · ${at.status.replace('_', ' ')}${at.roomNumber ? ` · ${t('common.room')} ${at.roomNumber}` : ''}`,
        }))}
      />
    </div>
  );
}
